import UserType from "@/enum/UserType";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import SharedEwaybillAccountTypeNameMap from "@/map/SharedEwaybillAccountTypeNameMap";
import { RechargeRecordResult } from "@/services/api/rechargeRecord";
import { getEwaybillAccountMapping } from "@/services/api/sharedEwaybillAccount";
import { SharedEwaybillQuantityChangeRecord } from "@/services/api/sharedEwaybillShippingAddress";
import { Table, TableProps } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import React from "react";

interface ShareRechargeRecordDetailTableProps extends TableProps<any> {
  userType: UserType;
  result: RechargeRecordResult;
}

const ShareRechargeRecordDetailTable: React.FC<ShareRechargeRecordDetailTableProps> = function (props) {
  const { userType, pagination, result, ...rest } = props;

  const { ewaybillShippingAddressMapping, ewaybillAccountMapping, branchAccountMapping, subscriptionMapping } =
    getEwaybillAccountMapping(result?.sharedEwaybillAccounts);

  const columns: ColumnsType<SharedEwaybillQuantityChangeRecord> = [
    {
      title: "我的面单账号",
      key: "platformUsername",
      width: 200,
      render: (_, record) => {
        const { sharedEwaybillShippingAddressId } = record;
        const account = ewaybillAccountMapping.get(sharedEwaybillShippingAddressId);
        const platformUsername = account?.platformUsername;
        const type = account?.type;

        const ewaybillTypeName = SharedEwaybillAccountTypeNameMap[type];

        return (
          <>
            <div className="text-small">
              <div>{platformUsername}</div>
              <div>{ewaybillTypeName}</div>
            </div>
          </>
        );
      },
    },
    {
      title: "快递",
      width: 400,
      render: (_, record) => {
        const { sharedEwaybillShippingAddressId } = record;
        const shippingAddress = ewaybillShippingAddressMapping.get(sharedEwaybillShippingAddressId);
        const subscription = subscriptionMapping.get(sharedEwaybillShippingAddressId);
        const branchAccount = branchAccountMapping.get(sharedEwaybillShippingAddressId);
        const { province, city, district, town, street } = shippingAddress || {};

        const logisticsCompany = subscription?.logisticsCompany;
        const logisticsCompanyName = LogisticsCompanyNameMap[logisticsCompany];

        const branchAccountNameArr = [];
        if (branchAccount?.branchName) {
          branchAccountNameArr.push(branchAccount.branchName);
        }
        if (branchAccount?.branchCode) {
          branchAccountNameArr.push(`(${branchAccount.branchCode})`);
        }
        if (branchAccount?.waybillNoTypeName) {
          branchAccountNameArr.push(`-${branchAccount.waybillNoTypeName}`);
        }

        const branchAccountName = branchAccountNameArr.join("");
        const shippingAddressName = [province, city, district, town, street].filter((it) => it).join("");

        return (
          <div className="text-small">{[logisticsCompanyName, branchAccountName, shippingAddressName].join("/")}</div>
        );
      },
    },
    {
      title: "充值时间时间",
      dataIndex: "dateCreated",
      render: (dateCreated) => {
        if (!dateCreated) {
          return null;
        }
        return <div>{moment(dateCreated).format("YYYY-MM-DD HH:mm:ss")}</div>;
      },
    },
    {
      title: "操作类型",
      key: "type",
      dataIndex: "quantity",
      render: (quantity, record, index) => {
        return <div>{quantity >= 0 ? "增加" : "减少"}</div>;
      },
    },
    {
      title: "数量",
      dataIndex: "quantity",
      render: (quantity, record, index) => {
        return <div>{Math.abs(quantity)}</div>;
      },
    },
    {
      title: userType === UserType.PROVIDER ? "使用面单的人" : "共享面单的人",
      key: "consumerCompanyName",
      width: 150,
      render: (_, record) => {
        const { sharedEwaybillShippingAddressId } = record;
        const shippingAddress = ewaybillShippingAddressMapping.get(sharedEwaybillShippingAddressId);
        const ewaybillAccount = ewaybillAccountMapping.get(shippingAddress.id);

        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            {shippingAddress?.consumerName ? (
              <div>
                <span className="fw700">{shippingAddress.consumerName}</span>
              </div>
            ) : null}

            {ewaybillAccount?.consumerCompanyName ? (
              <div className={"attentionColor"}>微信：{ewaybillAccount.consumerCompanyName}</div>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {result ? (
        <div className="mb10">
          总计：充值{result.increaseRechargeCount + result.reduceRechargeCount}
          次，充值单号{result.totalIncreaseQuantity - result.totalReduceQuantity}个。其中增加单号
          {result.increaseRechargeCount}次，共增加{result.totalIncreaseQuantity}
          个；减少单号
          {result.reduceRechargeCount}次，共减少{result.totalReduceQuantity}个
        </div>
      ) : null}

      <Table
        {...rest}
        rowKey="id"
        size="small"
        columns={columns}
        pagination={{
          ...pagination,
          showQuickJumper: true,
          showTotal: (total) => `总共 ${total} 个项目`,
          showSizeChanger: true,
          pageSizeOptions: ["10", "50", "100", "200"],
        }}
      />
    </div>
  );
};

export default React.memo(ShareRechargeRecordDetailTable);
